import { useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { actionQueue } from '@/lib/actionQueue';
import { ActionItem } from './ActionItem';
import { AnnouncePreview } from './AnnouncePreview';
import { HelpTip } from '@/components/ui/help-tip';

// "Akce" view: what the planner has to act on now. The goal is an empty queue.
export const ActionQueue = ({ sim, onOpen }) => {
  const queue = useMemo(() => actionQueue(sim.loadings), [sim.loadings]);
  // Everything that has been in the queue during this visit, so the counter can show progress.
  const seen = useRef(new Set());
  queue.forEach(({ loading }) => seen.current.add(loading.id));
  const total = seen.current.size;
  const done = total - queue.length;
  const [linkCarrier, setLinkCarrier] = useState(null);

  const urge = (id) => { sim.urgeOne(id); toast.success(t('actions.urgedToast')); };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-2" data-tour="actions-progress">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-semibold flex items-center gap-1">{t('actions.title')} <HelpTip id="actions" /></div>
          <div className="text-sm tabular-nums">{t('actions.progress')}: <span className="font-bold">{done} / {total}</span></div>
        </div>
        <Progress value={total ? (done / total) * 100 : 100} className="h-2" />
        <div className="text-xs text-muted-foreground">{t('actions.hint')}</div>
      </Card>

      {queue.length === 0 ? (
        <Card className="p-8 text-center space-y-2">
          <PartyPopper className="h-8 w-8 mx-auto text-emerald-600" />
          <div className="font-semibold">{t('actions.empty')}</div>
          <div className="text-sm text-muted-foreground">{t('actions.emptyHint')}</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {queue.map(({ loading, risk }) => (
            <ActionItem
              key={loading.id}
              loading={loading}
              risk={risk}
              onUrge={urge}
              onCall={onOpen}
              onLink={setLinkCarrier}
              onReplan={sim.applyPlan}
              onResolve={sim.resolve}
            />
          ))}
        </div>
      )}

      <AnnouncePreview
        carrier={linkCarrier}
        loadings={sim.loadings}
        open={!!linkCarrier}
        onOpenChange={(open) => !open && setLinkCarrier(null)}
        onSaved={sim.syncAnnouncements}
      />
    </div>
  );
};
